import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Navigate, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LayoutDashboard, Users, BookOpen, Trophy, Award, LogOut, Diamond, ExternalLink, Sparkles, Sun, ClipboardCheck, GraduationCap, KeyRound, Quote, Bell, Wallet, MessagesSquare, Headset, ClipboardList, HardDrive, Radio, Ticket, Mail, CalendarClock, Footprints, HeartHandshake, UserCog, Landmark, School, Shield, BookMarked, Bot } from "lucide-react";
import { isStaffOrAdmin, isSuperAdmin, logout, getUser, mediaUrl } from "../../lib/api";
import { NotificationBell } from "../../components/NotificationBell";
import { ErrorBoundary } from "../../components/ErrorBoundary";

const ADMIN_NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, section: "dashboard" },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users, section: "usuarios" },
  { to: "/admin/familias", label: "Familias Supera", icon: HeartHandshake, section: "familias" },
  { to: "/admin/referencias", label: "Referencias", icon: KeyRound, section: "referencias" },
  { to: "/admin/chat", label: "Chat Privado", icon: MessagesSquare, section: "chat" },
  { to: "/admin/chat-gestion", label: "Gestión Chat", icon: Headset, section: "chat" },
  { to: "/admin/compromisos", label: "Hoja Compromisos", icon: ClipboardList, section: "compromisos" },
  { to: "/admin/finanzas", label: "Financiamiento", icon: Wallet, section: "finanzas" },
  { to: "/admin/drive", label: "Supera Drive", icon: HardDrive, section: "drive" },
  { to: "/admin/connect", label: "Supera Connect", icon: Radio, section: "connect" },
  { to: "/admin/invitados", label: "Invitados Especiales", icon: Ticket, section: "invitados" },
  { to: "/admin/inversores", label: "Inversores Supera", icon: Landmark, section: "inversores" },
  { to: "/admin/docentes", label: "Docentes · Colegios", icon: School, section: "docentes" },
  { to: "/admin/fuerzas", label: "Fuerzas Especiales", icon: Shield, section: "fuerzas" },
  { to: "/admin/capacitaciones", label: "Capacitaciones", icon: BookMarked, section: "capacitaciones" },
  { to: "/admin/ana", label: "Ana", icon: Bot, section: "ana" },
  { to: "/admin/nexus", label: "Supera Nexus", icon: CalendarClock, section: "nexus" },
  { to: "/admin/carrera", label: "Carrera Mensual", icon: Footprints, section: "carrera" },
  { to: "/admin/correos", label: "Correos", icon: Mail, section: "correos" },
  { to: "/admin/notificaciones", label: "Notificaciones", icon: Bell, section: "notificaciones" },
  { to: "/admin/reflexiones", label: "Reflexiones", icon: Sun, section: "reflexiones" },
  { to: "/admin/evidencias", label: "Evidencias", icon: ClipboardCheck, section: "evidencias" },
  { to: "/admin/campus", label: "Campus Supera", icon: GraduationCap, section: "campus" },
  { to: "/admin/superax", label: "Supera X", icon: Sparkles, section: "superax" },
  { to: "/admin/graduacion", label: "Puntos Graduación", icon: Trophy, section: "graduacion" },
  { to: "/admin/frases", label: "Frases", icon: Quote, section: "frases" },
  { to: "/admin/frases-splash", label: "Frases Splash", icon: Quote, section: "frases-splash" },
  { to: "/admin/temas", label: "Temas", icon: BookOpen, section: "temas" },
  { to: "/admin/ligas", label: "Ligas", icon: Award, section: "ligas" },
  { to: "/admin/asistente", label: "Asistente IA", icon: Sparkles, section: "asistente" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const user = getUser();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);

  if (!isStaffOrAdmin()) {
    if (getUser()?.role === "inversor") return <Navigate to="/inversores" replace />;
    if (getUser()?.role === "docente") return <Navigate to="/docentes" replace />;
    return <Navigate to="/admin" replace />;
  }

  const permisos = user?.permisos || [];
  const NAV = isSuperAdmin()
    ? ADMIN_NAV
    : ADMIN_NAV.filter((n) => n.section === "dashboard" || permisos.includes(n.section));
  const signOut = () => { logout(); navigate("/admin"); };

  return (
    <div className="min-h-screen bg-[#F4F6F9] flex">
      {menuOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="admin-menu-overlay" />}
      {/* Sidebar */}
      <aside className={`w-72 lg:w-64 bg-navy flex-shrink-0 flex flex-col fixed inset-y-0 left-0 h-[100dvh] z-50 transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="admin-sidebar">
        <div className="h-16 lg:h-20 flex items-center gap-2 px-6 border-b border-white/10">
          <Diamond className="w-5 h-5 text-gold fill-[var(--gold)]" />
          <span className="font-extrabold tracking-[0.3em] text-white flex-1">SUPERA</span>
          <button className="lg:hidden text-white/70 p-1" onClick={() => setMenuOpen(false)} data-testid="admin-menu-close"><X className="w-6 h-6" /></button>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-6 space-y-1 admin-nav-scroll">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                    isActive ? "bg-[var(--gold)] text-[#0A1C30]" : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="w-5 h-5" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 pb-6 space-y-1">
          <NavLink to="/admin/perfil" className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-colors ${isActive ? "bg-[var(--gold)] text-[#0A1C30]" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            <UserCog className="w-5 h-5" /> Mi perfil
          </NavLink>
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-white/70 hover:bg-white/10 hover:text-white transition-colors">
            <ExternalLink className="w-5 h-5" /> Ver sitio
          </a>
          <button onClick={signOut} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-white/70 hover:bg-white/10 hover:text-white transition-colors">
            <LogOut className="w-5 h-5" /> Salir
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 lg:ml-64 min-w-0 admin-main">
        <header className="h-16 lg:h-20 bg-white border-b border-black/5 flex items-center justify-end gap-3 lg:gap-4 px-4 lg:px-8 sticky top-0 z-30">
          <button className="lg:hidden mr-auto w-10 h-10 rounded-xl bg-[#0A1C30] text-white grid place-items-center" onClick={() => setMenuOpen(true)} data-testid="admin-menu-toggle"><Menu className="w-5 h-5" /></button>
          <div className="[&_button]:!bg-[#0A1C30]/5 [&_svg]:!text-[#0A1C30]"><NotificationBell /></div>
          <NavLink to="/admin/perfil" className="flex items-center gap-3 group" data-testid="admin-header-profile">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-extrabold text-[#0A1C30] group-hover:text-[#B99B2E] transition-colors">{user?.nombre || "Admin"}</div>
              <div className="text-xs text-[#6B7688]">{user?.email}</div>
            </div>
            <span className="w-10 h-10 rounded-full bg-[#0A1C30] text-gold flex items-center justify-center font-extrabold overflow-hidden">
              {user?.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : (user?.nombre || "A").charAt(0)}
            </span>
          </NavLink>
        </header>
        <div className="p-3 sm:p-5 lg:p-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
